import { useMemo, useState } from 'react';

import { AgGridReact } from 'ag-grid-react';

import Icon from 'components/shared/Icon/Icon';

import AddItemsModal from './AddItemsModal';

const TABS = [
  { key: 'patients', label: 'Patients' },
  { key: 'services', label: 'Services' },
  { key: 'macros', label: 'Macros' },
  { key: 'packages', label: 'Packages' },
  { key: 'products', label: 'Products' }
];

const EmptyOverlay = ({ onAdd, label }) => (
  <div className="mt-10 flex h-full w-full flex-col items-center justify-center gap-4 py-8">
    <div className="flex h-20 w-20 items-center justify-center">
      <Icon icon="illustration-empty-table" />
    </div>
    <div className="flex flex-col items-center gap-1">
      <span className="text-base font-600 text-neutral-900">No {label} added</span>
      <span className="text-sm text-neutral-500">This is a preliminary draft helper text.</span>
    </div>
    <button
      onClick={onAdd}
      className="flex items-center gap-1 text-sm font-500 text-primary-500 hover:text-primary-600">
      <Icon icon="add-mono" size={16} color="primary" />
      Add {label}
    </button>
  </div>
);

const SpecificDataTabs = ({ practiceId, selectedLocation, importData, setImportData }) => {
  const [activeTab, setActiveTab] = useState('patients');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleAddItems = (items) => {
    setImportData((prev) => ({
      ...prev,
      [activeTab]: [...prev[activeTab], ...items]
    }));
    setIsModalOpen(false);
  };

  const currentTabData = importData[activeTab] || [];
  const currentLabel = TABS.find((t) => t.key === activeTab)?.label.toLowerCase();

  const columnDefs = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        flex: 1
      },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1
      }
    ],
    []
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: false
    }),
    []
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-lg border border-solid border-neutral-200">
      <div className="flex shrink-0 items-center justify-between border-b border-l-0 border-r-0 border-t-0 border-solid border-neutral-200 px-4">
        <div className="flex">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-500 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-neutral-600 hover:text-neutral-900'
              }`}>
              {tab.label}
              <span
                className={`rounded-full px-2 py-0.5 text-xs ${
                  activeTab === tab.key
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-neutral-100 text-neutral-600'
                }`}>
                {importData[tab.key]?.length || 0}
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-1 text-sm font-500 text-primary-500 hover:text-primary-600">
          <Icon icon="add-mono" size={16} color="primary" />
          Add {currentLabel}
        </button>
      </div>

      <div className="ag-theme-quartz min-h-0 flex-1 [&_.ag-body-viewport]:!overflow-y-auto [&_.ag-root-wrapper]:!h-full [&_.ag-root-wrapper]:!rounded-none [&_.ag-root-wrapper]:!border-0">
        <AgGridReact
          rowData={currentTabData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          domLayout={currentTabData.length === 0 ? 'autoHeight' : 'normal'}
          headerHeight={40}
          rowHeight={48}
          noRowsOverlayComponent={() => (
            <EmptyOverlay onAdd={() => setIsModalOpen(true)} label={currentLabel} />
          )}
        />
      </div>

      <AddItemsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddItems}
        title={`Add ${currentLabel}`}
        subtitle={`Search and select ${currentLabel} from the list`}
        practiceId={practiceId}
        selectedLocation={selectedLocation}
        tableType={activeTab}
        existingItems={currentTabData}
      />
    </div>
  );
};

export default SpecificDataTabs;
